import { Toggle, Tooltip, NumberInput } from '@ets/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'utils/toast';
import styles from './Toolbar.module.scss';

type PageNavigatorProps = {
  onPageUp: () => void;
  onPageDown: () => void;
  goToPage: (page: number) => void;
  totalPageNumber: number;
  currentPageNumber: number;
};

const PageNavigator = ({
  onPageUp,
  onPageDown,
  totalPageNumber,
  goToPage,
  currentPageNumber,
}: PageNavigatorProps) => {
  const intl = useIntl();
  const [localPageNumber, setLocalPageNumber] = useState('1');

  useEffect(() => {
    if (+localPageNumber !== currentPageNumber) {
      setLocalPageNumber(`${currentPageNumber}`);
    }
  }, [currentPageNumber]);

  const inputWidth = useMemo(() => {
    const totalPageNumberDigits = (Math.log(+totalPageNumber) * Math.LOG10E + 1) | 0;
    return totalPageNumberDigits < 2 ? `32px` : undefined;
  }, [totalPageNumber]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (!Number.isNaN(+localPageNumber) && +localPageNumber !== currentPageNumber) {
        if (+localPageNumber <= totalPageNumber && +localPageNumber >= 1) {
          goToPage(+localPageNumber);
        } else {
          toast({
            variant: 'error',
            title: 'WRONG_PAGE_NUMBER',
            message: 'PAGE_NUMBER_ERROR',
            messageValues: { number: localPageNumber },
          });
          setLocalPageNumber(`${currentPageNumber}`);
        }
      } else {
        setLocalPageNumber(`${currentPageNumber}`);
      }
    }
  };

  return (
    <>
      <Tooltip
        placement="top"
        content={intl.formatMessage({ id: 'PREVIOUS_PAGE' })}
        testId="menu-previous-page"
        visible={currentPageNumber !== 1}
      >
        <Toggle
          style={{ margin: '0 0.25rem 0 0' }}
          variant="link"
          size="m"
          iconProps={{ icon: 'chevron-up' }}
          onToggle={onPageUp}
          testId="menu-previous-page"
          disabled={currentPageNumber === 1}
        />
      </Tooltip>
      <Tooltip
        placement="top"
        content={intl.formatMessage({ id: 'NEXT_PAGE' })}
        testId="menu-next-page"
        visible={currentPageNumber !== totalPageNumber}
      >
        <Toggle
          style={{ margin: '0 0.75rem 0 0' }}
          variant="link"
          size="m"
          onToggle={onPageDown}
          iconProps={{ icon: 'chevron-down' }}
          testId="menu-next-page"
          disabled={currentPageNumber === totalPageNumber}
        />
      </Tooltip>
      <NumberInput
        type="number"
        size="m"
        placeholder="1"
        width={inputWidth}
        margin="0 0.5rem 0 0"
        max={`${totalPageNumber}`}
        min="1"
        isClearVisible={false}
        value={`${localPageNumber}`}
        onChange={(e) => setLocalPageNumber(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (+localPageNumber !== currentPageNumber) {
            setLocalPageNumber(`${currentPageNumber}`);
          }
        }}
        testId="page"
      />
      <span className={styles.totalPages}>/ {totalPageNumber}</span>
    </>
  );
};

export default PageNavigator;
